import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getServices, getAddons } from '../api';

export default function ServicesPage() {
  const { settings } = useTheme();
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getServices(), getAddons()])
      .then(([svc, add]) => {
        setServices(svc);
        setAddons(add);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const freeKm = settings.delivery_free_km || '15';
  const feePerKm = settings.delivery_fee_per_km || '2.50';
  const maxKm = settings.delivery_max_km || '50';

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        {settings.hero_image && (
          <img src={settings.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)]/80 via-[var(--color-primary)]/60 to-black/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            Our Services
          </h1>
          <p className="mt-6 text-lg text-gray-200 max-w-2xl mx-auto">
            From quick sessions to full-day events, we have the perfect sauna experience for you.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-[var(--color-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
              <p className="mt-4 text-[var(--color-text-light)]">Loading services...</p>
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-[var(--color-text)] mb-4 text-center">
                Sauna Sessions
              </h2>
              <p className="text-center text-[var(--color-text-light)] mb-14 max-w-2xl mx-auto">
                Every session includes delivery, setup, firewood, stove firing, and full takedown. Just show up and sweat.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {services.map((service, index) => (
                  <div
                    key={service.id}
                    className={`rounded-2xl p-8 border transition-shadow hover:shadow-lg ${
                      index === 1
                        ? 'bg-[var(--color-primary)] text-white border-transparent shadow-lg scale-[1.02]'
                        : 'bg-[var(--color-bg)] border-gray-200 shadow-sm'
                    }`}
                  >
                    {index === 1 && (
                      <span className="inline-block px-3 py-1 bg-[var(--color-accent)] text-white text-xs font-bold uppercase rounded-full mb-4 tracking-wider">
                        Most Popular
                      </span>
                    )}
                    <h3 className={`text-2xl font-bold mb-2 ${index === 1 ? 'text-white' : 'text-[var(--color-text)]'}`}>
                      {service.name}
                    </h3>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className={`text-4xl font-extrabold ${index === 1 ? 'text-[var(--color-warm)]' : 'text-[var(--color-accent)]'}`}>
                        ${service.price}
                      </span>
                      <span className={`text-sm ${index === 1 ? 'text-gray-300' : 'text-[var(--color-text-light)]'}`}>
                        / {service.duration_hours}hr{service.duration_hours !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <p className={`mb-8 leading-relaxed ${index === 1 ? 'text-gray-200' : 'text-[var(--color-text-light)]'}`}>
                      {service.description}
                    </p>
                    <Link
                      to="/book"
                      className={`block w-full text-center px-6 py-3 rounded-lg font-semibold transition-opacity hover:opacity-90 ${
                        index === 1
                          ? 'bg-[var(--color-accent)] text-white'
                          : 'bg-[var(--color-primary)] text-white'
                      }`}
                    >
                      Book Now
                    </Link>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Add-ons */}
      {addons.length > 0 && (
        <section className="py-20 bg-[var(--color-bg-alt)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[var(--color-text)] mb-4 text-center">
              Enhance Your Experience
            </h2>
            <p className="text-center text-[var(--color-text-light)] mb-14 max-w-2xl mx-auto">
              Add extras to make your session even better.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {addons.map((addon) => (
                <div
                  key={addon.id}
                  className="bg-[var(--color-bg)] rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold text-[var(--color-text)]">{addon.name}</h3>
                    <span className="text-lg font-bold text-[var(--color-accent)] whitespace-nowrap ml-3">
                      +${addon.price}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--color-text-light)] leading-relaxed">
                    {addon.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Delivery Info */}
      <section className="py-20 bg-[var(--color-bg)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[var(--color-text)] mb-10 text-center">
            Delivery Information
          </h2>
          <div className="bg-[var(--color-bg-alt)] rounded-2xl p-8 sm:p-10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center mb-8">
              <div>
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-2xl font-bold text-[var(--color-text)]">{freeKm} km</p>
                <p className="text-sm text-[var(--color-text-light)] mt-1">Free delivery radius</p>
              </div>
              <div>
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-2xl font-bold text-[var(--color-text)]">${feePerKm}/km</p>
                <p className="text-sm text-[var(--color-text-light)] mt-1">Beyond free radius</p>
              </div>
              <div>
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
                  </svg>
                </div>
                <p className="text-2xl font-bold text-[var(--color-text)]">{maxKm} km</p>
                <p className="text-sm text-[var(--color-text-light)] mt-1">Maximum delivery range</p>
              </div>
            </div>
            <p className="text-[var(--color-text-light)] text-center leading-relaxed">
              We deliver our trailer sauna directly to your location within the Penticton and South Okanagan area.
              Delivery within {freeKm} km of Penticton is included free with every booking. For locations further out,
              a fee of ${feePerKm} per km applies. We service locations up to {maxKm} km away.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-text)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Book?
          </h2>
          <p className="text-gray-200 text-lg mb-10 max-w-xl mx-auto">
            Pick your session, choose your add-ons, and lock in your date.
          </p>
          <Link
            to="/book"
            className="inline-block px-10 py-4 bg-[var(--color-accent)] text-white text-lg font-semibold rounded-xl shadow-lg hover:opacity-90 transition-all hover:shadow-xl hover:-translate-y-0.5"
          >
            Book Now
          </Link>
        </div>
      </section>
    </div>
  );
}
